import { ProblemJsonError } from "@pushback-and-chaos/client";
import type { AccountErasureRequested, WhoAmI } from "@pushback-and-chaos/contracts";

/**
 * Whether settings shows "delete my account." (docs/ACCOUNT-ERASURE-DESIGN-2026-10-20.md): a
 * signed-in account only. A guest never sees it, since a guest's engagements delete themselves
 * after 30 days, and nobody has nothing to delete. Unlike the account export beside it, it does
 * not open for a stored guest id. Kept here so a test holds the rule.
 */
export function erasureDoorOpen(kind: WhoAmI["kind"] | undefined): boolean {
  return kind === "user";
}

/**
 * The date a pending deletion runs after, from the server's identity, or null when nothing is
 * pending (a guest, nobody, an account that kept itself, or an identity not yet read). Set, the
 * root shows the grace notice instead of the app.
 */
export function pendingErasure(identity: WhoAmI | undefined): string | null {
  return identity?.erasure?.eraseAfter ?? null;
}

/**
 * What "delete my account." came to, for the dialog: made (the app logs this browser out and shows
 * the notice); failed, with nothing changed; or unsure, when nothing could say which.
 */
export type ErasureConfirmResult = "requested" | "failed" | "unsure";

/**
 * What the notice after confirming can say. From the server's answer, both. When that answer was
 * lost and the identity read says the request went through, the date only if the identity holds
 * it, and never the email's state: whether a confirmation was queued is not known then.
 */
export interface ErasureRequestedFacts {
  readonly eraseAfter: string | null;
  readonly email: Pick<AccountErasureRequested, "copiesByEmail" | "emailQueued"> | null;
}

export type ErasureRequestOutcome =
  | { readonly kind: "requested"; readonly facts: ErasureRequestedFacts }
  | { readonly kind: "failed" }
  | { readonly kind: "unsure" };

/**
 * POST /me/erasure did not answer with success. It can commit and lose its answer on the way back,
 * and the account is then scheduled and signed out everywhere: "nothing has changed" would be
 * untrue. So:
 *
 * - a refusal the server answered (a 4xx: the per-account limit's 429, a guest's 409, a malformed
 *   request) is a real failure, and nothing has changed;
 * - a 401 says this browser's sign-in is gone, which this request, one from another device or an
 *   expiry would all leave the same: unsure, claiming nothing;
 * - anything else (no connection, a server or gateway error, an unreadable reply) may be a lost
 *   answer, settled by asking who this is, outside the cache so the root does not swap the app
 *   for the grace notice under the dialog:
 *   - this account, with a deletion: made, with its date;
 *   - no account (nobody, or a guest cookie): the sign-ins were deleted, so it was made, date
 *     unknown;
 *   - this account, nothing pending: a real failure;
 *   - another account, an account the dialog never knew, or no answer: unsure.
 */
export async function settleErasureRequest(caught: unknown, readIdentity: () => Promise<WhoAmI>, account: string | null): Promise<ErasureRequestOutcome> {
  if (caught instanceof ProblemJsonError && caught.status === 401) return { kind: "unsure" };
  if (caught instanceof ProblemJsonError && caught.status >= 400 && caught.status < 500) return { kind: "failed" };
  let identity: WhoAmI;
  try {
    identity = await readIdentity();
  } catch {
    return { kind: "unsure" };
  }
  if (identity.kind !== "user") return { kind: "requested", facts: { eraseAfter: null, email: null } };
  if (account === null || identity.learnerId !== account) return { kind: "unsure" };
  if (identity.erasure !== null) return { kind: "requested", facts: { eraseAfter: identity.erasure.eraseAfter, email: null } };
  return { kind: "failed" };
}
